/**
 * @file AttributeTable.js
 * @description Component for rendering and managing a table of attributes in the Multi-tenant SSO
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import SSOAttributeValueDropdown from '../../shared/asyncDropdowns/SSOAttributeValueDropdown';
import style from '../sso.module.scss';
import { getText } from '../../../i18n';

const AttributeTable = ({
    attributes,
    onChange,
    isClient = false,
    readOnly = false,
}) => {
    const [localAttributes, setLocalAttributes] = useState(
        attributes?.length
            ? mapAttributesForUI(attributes)
            : [createNewAttribute()],
    );
    const initialRenderRef = useRef(true);
    const hasInteractedRef = useRef(false);

    useEffect(() => {
        if (initialRenderRef.current) {
            initialRenderRef.current = false;
            onChange(mapAttributesForAPI(localAttributes));
        }
    }, []);

    useEffect(() => {
        if (!initialRenderRef.current && !hasInteractedRef.current) {
            setLocalAttributes(mapAttributesForUI(attributes));
        }
    }, [attributes]);

    const getExcludedValues = useCallback(
        currentIndex => {
            return localAttributes.reduce((acc, attr, index) => {
                if (
                    index !== currentIndex &&
                    attr.ssoAttributeValueId &&
                    attr.ssoAttributeValueId !== 'literal'
                ) {
                    acc.add(attr.ssoAttributeValueId);
                }
                return acc;
            }, new Set());
        },
        [localAttributes],
    );

    const handleAttributeChange = useCallback(
        (index, field, value) => {
            if (readOnly) {
                return;
            }
            if (
                isClient &&
                (field === 'attribute' || field === 'description')
            ) {
                return;
            }
            if (
                isClient &&
                localAttributes[index].isLocked &&
                (field === 'ssoAttributeValueId' || field === 'literal')
            ) {
                return;
            }
            hasInteractedRef.current = true;
            setLocalAttributes(prevAttributes => {
                const updatedAttributes = prevAttributes.map((attr, i) => {
                    if (i === index) {
                        const updatedAttr = { ...attr, [field]: value };
                        if (field === 'ssoAttributeValueId') {
                            updatedAttr.literal =
                                value === 'literal' ? '' : null;
                        }
                        if (isClient) {
                            updatedAttr.isEdited = true;
                        }
                        return updatedAttr;
                    }
                    return attr;
                });
                onChange(mapAttributesForAPI(updatedAttributes));
                return updatedAttributes;
            });
        },
        [isClient, localAttributes, onChange, readOnly],
    );

    const addAttribute = useCallback(
        index => {
            if (isClient || readOnly) return;
            hasInteractedRef.current = true;
            setLocalAttributes(prevAttributes => {
                const updatedAttributes = [
                    ...prevAttributes.slice(0, index + 1),
                    createNewAttribute(),
                    ...prevAttributes.slice(index + 1),
                ];
                onChange(mapAttributesForAPI(updatedAttributes));
                return updatedAttributes;
            });
        },
        [isClient, onChange, readOnly],
    );

    const removeAttribute = useCallback(
        index => {
            if (isClient || readOnly) return;
            hasInteractedRef.current = true;
            setLocalAttributes(prevAttributes => {
                const updatedAttributes = prevAttributes.filter(
                    (_, i) => i !== index,
                );
                onChange(mapAttributesForAPI(updatedAttributes));
                return updatedAttributes;
            });
        },
        [isClient, onChange, readOnly],
    );

    return (
        <div className={style.attributeTable}>
            {localAttributes.map((attr, index) => (
                <div
                    key={`${attr.id}-${index}`}
                    className="attribute-row d-flex position-relative"
                >
                    {attr.isLocked && (
                        <div className={`position-absolute ${style.lockedRow}`}>
                            <i className="bi bi-lock-fill text-muted me-2"></i>
                        </div>
                    )}
                    <div className={`form-group flex-lg-nowrap col-3 pe-2`}>
                        <label htmlFor={`attribute-${index}`}>
                            {getText('msg_a086d942884a')}
                            <span className="text-danger">*</span>
                        </label>
                        <input
                            type="text"
                            id={`attribute-${index}`}
                            name="attribute"
                            className="form-control"
                            value={attr.attribute}
                            onChange={e =>
                                handleAttributeChange(
                                    index,
                                    'attribute',
                                    e.target.value,
                                )
                            }
                            required
                            placeholder={getText('msg_9e26e16dd00e')}
                            maxLength={256}
                            readOnly={isClient || readOnly}
                            disabled={isClient || readOnly}
                        />
                    </div>

                    <div className={`form-group flex-lg-nowrap col-3 pe-2`}>
                        <label htmlFor={`description-${index}`}>
                            {getText('msg_55f8ebc805e6')}
                        </label>
                        <input
                            type="text"
                            id={`description-${index}`}
                            name="description"
                            className="form-control"
                            value={attr.description}
                            onChange={e =>
                                handleAttributeChange(
                                    index,
                                    'description',
                                    e.target.value,
                                )
                            }
                            required
                            placeholder={getText('msg_8ee108507aa0')}
                            maxLength={50}
                            readOnly={isClient || readOnly}
                            disabled={isClient || readOnly}
                        />
                    </div>

                    <div className={`form-group flex-lg-nowrap col-3 pe-2`}>
                        <SSOAttributeValueDropdown
                            selectedAttributeValue={attr.ssoAttributeValueId}
                            onAttributeValueChange={value =>
                                handleAttributeChange(
                                    index,
                                    'ssoAttributeValueId',
                                    value,
                                )
                            }
                            inlineLabel={false}
                            label={getText('msg_8dce170de238')}
                            isReadOnly={readOnly || (isClient && attr.isLocked)}
                            isDisabled={readOnly || (isClient && attr.isLocked)}
                            addLiteral={true}
                            // excludedValues={getExcludedValues(index)}
                        />
                    </div>

                    {attr.ssoAttributeValueId === 'literal' && (
                        <div className="form-group flex-grow-1 me-2 col-1">
                            <label htmlFor={`literal-${index}`}>
                                {getText('msg_92e97e035f44')}
                            </label>
                            <input
                                type="text"
                                id={`literal-${index}`}
                                className="form-control"
                                value={attr.literal || ''}
                                onChange={e =>
                                    handleAttributeChange(
                                        index,
                                        'literal',
                                        e.target.value,
                                    )
                                }
                                placeholder={getText('msg_cc74ae1ccf46')}
                                readOnly={
                                    readOnly || (isClient && attr.isLocked)
                                }
                                disabled={
                                    readOnly || (isClient && attr.isLocked)
                                }
                            />
                        </div>
                    )}

                    {!isClient && !readOnly && (
                        <div
                            className={`d-flex align-items-end ${style.attActionItems}`}
                        >
                            <button
                                type="button"
                                className="btn btn-link p-0"
                                onClick={() =>
                                    handleAttributeChange(
                                        index,
                                        'isLocked',
                                        !attr.isLocked,
                                    )
                                }
                            >
                                <i
                                    className={`bi bi-${
                                        attr.isLocked
                                            ? 'unlock-fill'
                                            : 'lock-fill'
                                    }`}
                                ></i>
                            </button>

                            {localAttributes.length > 1 && (
                                <button
                                    type="button"
                                    className="btn btn-link p-0 text-danger"
                                    onClick={() => removeAttribute(index)}
                                >
                                    <i className="bi bi-trash3"></i>
                                </button>
                            )}

                            <button
                                type="button"
                                className="btn btn-link p-0 me-2 text-success"
                                onClick={() => addAttribute(index)}
                            >
                                <i className="bi bi-plus-circle"></i>
                            </button>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

function createNewAttribute() {
    return {
        id: 0,
        attribute: '',
        description: '',
        ssoAttributeValueId: '',
        literal: null,
        isLocked: false,
        isEdited: false,
    };
}

function mapAttributesForUI(attributes) {
    return attributes.map(attr => ({
        ...attr,
        ssoAttributeValueId:
            attr.ssoAttributeValueId === null && attr.literal !== null
                ? 'literal'
                : attr.ssoAttributeValueId,
        isEdited: false,
    }));
}

function mapAttributesForAPI(attributes) {
    return attributes.map(attr => {
        const apiAttr = { ...attr };
        if (apiAttr.ssoAttributeValueId === 'literal') {
            apiAttr.ssoAttributeValueId = null;
        } else {
            apiAttr.literal = null;
        }

        return apiAttr;
    });
}

export default AttributeTable;
